import { screenshotIsRendering } from '@/constants';
import {
	getPageUrl,
	getPageSearch,
	getUrlParam,
	getCookie,
	getJsFromString,
	getTextContent,
	getDataAttr,
	log
} from '@/utils';
import { isNestedStateProp } from './';

function trim(str) {
	return String(str).trim();
}

const operatorFn = {
	//equals
	'===': (x, y) => trim(x) === trim(y),
	//does not equal
	'!==': (x, y) => trim(x) !== trim(y),
	//contains
	'*': (x, y) => y.indexOf(trim(x)) > -1,
	//does not contain
	'!*': (x, y) => (!x && y.length > 0) || y.indexOf(trim(x)) === -1,
	is_not_empty: y => y.length > 0
};

//global/starting logic
const startingOperatorFn = {
	url: x => getPageUrl().indexOf(x) > -1,
	title: x => document.title.indexOf(x) > -1,
	url_query: x => getUrlParam(x, getPageSearch()),
	cookie: x => getCookie(x),
	js: x => getJsFromString(x),
	css_selector: x => getTextContent(x),
	data_attr: x => getDataAttr(x)
};

//Get the value returned from the starting logic operator
function getStartingOperatorReturnValue(action = {}) {
	const operatorReturnValue = startingOperatorFn[action?.source]?.(action?.key) ?? null;
	return operatorReturnValue;
}

//test if stating logic rule is valid
function isValidStartingRule(action = {}) {
	const operatorReturnValue = startingOperatorFn[action?.source]?.(action?.key) ?? null;
	if (!operatorReturnValue) {
		return false;
	}

	if (Array.isArray(action.values) && action.values.length > 0) {
		return action.values.filter(value => operatorReturnValue === value).length > 0;
	}

	if (typeof action.value === 'string' && action.value.length > 0) {
		return operatorReturnValue === action.value;
	}

	return operatorReturnValue;
}

function getElementExtraKey(elementKey, block = {}) {
	try {
		if (
			Number(elementKey) > Object.keys(block.properties.elements ?? {}).length &&
			Object.keys(block.properties.elements_extra ?? {}).length > 0
		) {
			const extraElementKey =
				Number(elementKey) - Object.keys(block.properties.elements ?? {}).length;
			if (block.properties.elements_extra[extraElementKey]) {
				return extraElementKey;
			}
		}
	} catch (e) {
		log('Error getting element extra key', e);
	}

	return null;
}

function getCheckboxDatafield(elementKey, block = {}) {
	const elementExtraKey = getElementExtraKey(elementKey, block);
	if (elementExtraKey !== null) {
		return block.properties.elements_extra[elementExtraKey]?.data_field;
	}
	return block.properties.elements[elementKey]?.data_field;
}

function getValue(elementKey, block = {}) {
	if (
		['gcr', 'radio', 'checkbox', 'select', 'thumbs', 'category'].indexOf(block.typeName) >
		-1
	) {
		const elementExtraKey = getElementExtraKey(elementKey, block);
		if (elementExtraKey !== null) {
			return (
				block.properties.elements_extra?.[elementExtraKey]?.value ??
				block.properties.elements_extra?.[elementExtraKey]?.label
			);
		}
		return (
			block.properties.elements[elementKey]?.value ??
			block.properties.elements[elementKey]?.label
		);
	}

	return String(elementKey);
}

//test if question logic rule is valid
function isValidRule({ state, condition = {}, block = {}, didExecuteLogic } = {}) {
	if (condition.always && !didExecuteLogic) {
		return true;
	}

	const operator = operatorFn[condition?.operator];
	if (!operator) {
		return false;
	}

	if (condition.operator === 'is_not_empty') {
		return operator(state.value);
	}

	if (Array.isArray(condition?.values) && condition.values?.length > 0) {
		return (
			condition.values.filter(value => {
				return operator(value, state.value);
			}).length > 0
		);
	}

	if (condition.hasOwnProperty('trigger_element')) {
		return operator(condition.trigger_element, state.value);
	}

	const conditionsTrue = condition.elements?.filter(elementKey => {
		if (block.typeName === 'screenshot') {
			return operator(screenshotIsRendering, state.value);
		}

		if (block.typeName === 'checkbox') {
			return operator(true, state[getCheckboxDatafield(elementKey, block)]?.value);
		}

		const valueFromBlock = getValue(elementKey, block);

		return operator(valueFromBlock, state.value);
	});

	return condition?.concat !== '&&'
		? conditionsTrue?.length > 0
		: conditionsTrue?.length === condition.testValue?.length;
}

function getTargetId({ target = '', state = {} }) {
	const fieldAsArr = String(target).split('_');

	//test if the last item in the array contains only letters -> if so it's subelement and we have to get the second-last otherwise the last item in the array
	const idOrUuid = /^[a-z]+$/i.test(fieldAsArr[fieldAsArr.length - 1])
		? fieldAsArr[fieldAsArr.length - 2]
		: fieldAsArr[fieldAsArr.length - 1];

	if (fieldAsArr[0] === 'thanks-page') {
		return fieldAsArr[1];
	}
	return Number(state[idOrUuid]?.id);
}

function clearNestedStateValues(state = {}) {
	return Object.keys(state).reduce((newNestedState, currentKey) => {
		if (isNestedStateProp(currentKey)) {
			newNestedState[currentKey] = { ...state[currentKey] };
			newNestedState[currentKey].value = '';
		}

		return newNestedState;
	}, {});
}

function sortAlwaysRules(rules = []) {
	const sorted = [];
	const always = [];
	for (let i = 0; i < rules.length; i++) {
		const rule = rules[i];
		if (rule?.condition?.always) {
			always.push(rule);
		} else {
			sorted.push(rule);
		}
	}
	return [...sorted, ...always];
}

//apply question logic
export function applyLogic({ state, block = {}, rules = [], layout }) {
	if (!block.rules?.length) {
		return {
			newState: state
		};
	}

	let executeSubmitLogic = false;
	let didExecuteLogic = false;
	const elementsToBeUpdated = {};

	//always logic must be executed last and ONLY if other logic did not execute
	sortAlwaysRules(rules).forEach(({ action = {}, condition = {} } = {}) => {
		if (action.action === 'submit') {
			executeSubmitLogic = isValidRule({
				state: state[block.id],
				condition,
				block
			});
			return;
		}

		if (['hide', 'show'].indexOf(action.action) > -1) {
			action.targets.forEach(target => {
				const targetId = getTargetId({ target, state });
				if (!targetId) {
					return;
				}

				const valid = isValidRule({
					state: state[block.id],
					condition,
					block,
					didExecuteLogic
				});
				const show = action.action === 'show';
				const isVisible = valid ? show : !show;

				if (valid) {
					didExecuteLogic = true;
				}

				elementsToBeUpdated[targetId] = {
					isVisible,
					//remove value if el is being hidden
					...(!isVisible && {
						value: '',
						...clearNestedStateValues(state[targetId] ?? {}) //remove values from nested fields
					})
				};
			});

			return;
		}

		if (action.action === 'jump_to') {
			const targetId = getTargetId({ target: action.target, state });
			if (!targetId) {
				return;
			}

			const valid = isValidRule({
				state: state[block.id],
				condition,
				block,
				didExecuteLogic
			});

			if (valid) {
				didExecuteLogic = true;
				//with jump logic we have to set all elements between the current one and the jump to one to hidden

				const targetIndex = layout.indexOf(targetId);
				const blockIndex = layout.indexOf(Number(block.id));
				//backwards jump only toggle showInConvo status and value
				const isBackwardsJump = targetIndex < blockIndex;

				const layoutSlice = isBackwardsJump
					? layout.slice(targetIndex, blockIndex + 1)
					: layout.slice(blockIndex + 1, targetIndex);

				layoutSlice.forEach((blockId, index) => {
					elementsToBeUpdated[blockId] = {
						...(!isBackwardsJump && {
							isVisible: false
						}),
						showInConvo: isBackwardsJump ? index === 0 : false,
						isSkipped: false,
						value: ''
					};
				});
				return;
			}
		}
	});

	return {
		updateFromLogic: elementsToBeUpdated,
		submitFromLogic: executeSubmitLogic,
		newState: mergeLogicUpdates(state, elementsToBeUpdated)
	};
}

function mergeLogicUpdates(prevState, updateFromLogic) {
	const updatedState = {};
	try {
		Object.keys(updateFromLogic || {}).forEach(blockId => {
			if (prevState[blockId]) {
				updatedState[blockId] = {
					...prevState[blockId],
					...updateFromLogic[blockId]
				};
			}
		});
	} catch (e) {}

	return {
		...prevState,
		...updatedState
	};
}

//apply starting logic
export function applyStartingLogic({ state, layout, surveyRules = {} }) {
	const elementsToBeUpdated = {};

	Object.keys(surveyRules).forEach(ruleKey => {
		const { action } = surveyRules[ruleKey] ?? {};

		if (!isValidStartingRule(action)) {
			return;
		}

		if (['hide', 'show'].indexOf(action.action) > -1) {
			action.targets.forEach(target => {
				const targetId = getTargetId({ target, state });
				if (!targetId) {
					return;
				}

				const isVisible = action.action === 'show';

				elementsToBeUpdated[targetId] = {
					isVisible,
					//remove value if el is being hidden
					...(!isVisible && {
						value: ''
					})
				};
			});
			return;
		}

		if (action.action === 'jump_to') {
			const targetId = getTargetId({ target: action.target, state });
			if (!targetId) {
				return;
			}

			const targetIndex = layout.indexOf(targetId);
			const layoutSlice = layout.slice(0, targetIndex);

			layoutSlice.forEach((blockId, index) => {
				elementsToBeUpdated[blockId] = {
					isVisible: false,
					showInConvo: false,
					isSkipped: false,
					value: ''
				};
			});
			return;
		}

		if (action.action === 'prefill') {
			const prefillValue = getStartingOperatorReturnValue(action);
			action.targets.forEach(target => {
				const targetId = getTargetId({ target, state });
				if (!targetId) {
					return;
				}

				elementsToBeUpdated[targetId] = {
					...(elementsToBeUpdated[targetId] && {
						...elementsToBeUpdated[targetId]
					}),
					value: prefillValue,
					wasPrefilled: true
				};
			});
			return;
		}
	});

	return {
		newState: mergeLogicUpdates(state, elementsToBeUpdated),
		updateFromLogic: elementsToBeUpdated
	};
}
